import { slicedToArray as _slicedToArray, objectSpread2 as _objectSpread2 } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import React__default, { useEffect } from 'react';
import { useSearchContext, DEFAULT_MIN_QUERY_LENGTH } from '../../../searchContext/SearchContextProvider.js';
import localize from '../../../util/localize.js';
import { SearchResultsDisplay } from '../SearchResultsDisplay.js';
import { createCtx } from '../../../util/createCtx.js';
import { ANALYTICS_WRAPPER_CLASS } from '../../../util/analyticsConstants.js';
import { useScreenClass, Box, tokens, VisuallyHidden, GridContainer, Link } from '@vp/swan';
import { displayConfigurationDecider } from './searchBarDisplayConfigurations.js';
import { useRecentSearch } from '../../../localStorage/useRecentSearch.js';

var FlyoutFooter = function FlyoutFooter(_ref) {
  var seeAllText = _ref.seeAllText;
  var _useSearchContext = useSearchContext(),
    query = _useSearchContext.query,
    allResultsUrl = _useSearchContext.allResultsUrl,
    locale = _useSearchContext.locale,
    handleKeyDown = _useSearchContext.handleKeyDown,
    preventBlur = _useSearchContext.preventBlur;
    _useSearchContext.applicationName;
  var _useRecentSearch = useRecentSearch(),
    addRecentSearch = _useRecentSearch.addRecentSearch;
  var track = function track(evt) {
    if (window.tracking) {
      var _appRoot$dataset;
      var appRoot = evt.currentTarget.closest(".".concat(ANALYTICS_WRAPPER_CLASS));
      var application = appRoot === null || appRoot === void 0 || (_appRoot$dataset = appRoot.dataset) === null || _appRoot$dataset === void 0 ? void 0 : _appRoot$dataset.searchApplicationName;
      window.tracking.track('Search Submitted', {
        query: query,
        application: application,
        location: window.location.pathname
      });
    }
  };
  return /*#__PURE__*/React__default.createElement(Link, {
    key: "search-flyout-footer",
    href: allResultsUrl,
    tabIndex: -1,
    className: "search-flyout-footer-link",
    skin: "cta",
    onClick: function onClick(evt) {
      addRecentSearch(query);
      track(evt);
    },
    onKeyDown: function onKeyDown(evt) {
      if (evt.code === 'Enter') {
        track(evt);
      }
      handleKeyDown(evt);
    },
    onMouseDown: preventBlur,
    mt: 3,
    mb: 5,
    ml: 5,
    pt: 2,
    pr: 2,
    pl: 2,
    fontSkin: "body-small-bold"
  }, seeAllText || localize('allResultsText', locale));
};
var _createCtx = createCtx(),
  _createCtx2 = _slicedToArray(_createCtx, 2),
  useFlyoutContext = _createCtx2[0],
  FlyoutContext = _createCtx2[1];
var SearchFlyout = function SearchFlyout(_ref2) {
  var veilEnabled = _ref2.veilEnabled,
    _ref2$minQueryLen = _ref2.minQueryLen,
    minQueryLen = _ref2$minQueryLen === void 0 ? DEFAULT_MIN_QUERY_LENGTH : _ref2$minQueryLen,
    siteAnalyticsEventName = _ref2.siteAnalyticsEventName,
    _ref2$getSiteAnalytic = _ref2.getSiteAnalyticsQueryString,
    getSiteAnalyticsQueryString = _ref2$getSiteAnalytic === void 0 ? function (query) {
      return "swsquery=".concat(query, "&querycat=search_dropdown");
    } : _ref2$getSiteAnalytic,
    seeAllText = _ref2.seeAllText,
    applicationName = _ref2.applicationName,
    resultDisplayConfiguration = _ref2.resultDisplayConfiguration;
  var _useSearchContext2 = useSearchContext(),
    results = _useSearchContext2.results,
    query = _useSearchContext2.query,
    isFlyoutExpanded = _useSearchContext2.isFlyoutExpanded,
    setIsFlyoutExpanded = _useSearchContext2.setIsFlyoutExpanded,
    isFocused = _useSearchContext2.isFocused,
    selectedResult = _useSearchContext2.selectedResult,
    allResultsUrl = _useSearchContext2.allResultsUrl,
    instanceId = _useSearchContext2.instanceId,
    locale = _useSearchContext2.locale;
  var screenClass = useScreenClass();
  var flyoutPaddingPixels = Object.keys(results).some(function (taxonomy) {
    return results[taxonomy].length;
  }) && screenClass === 'xs' ? 3 : 0;
  useEffect(function () {
    setIsFlyoutExpanded(Object.keys(results).length > 0 && allResultsUrl.length > 0 && query.length >= minQueryLen);
  }, [setIsFlyoutExpanded, results, allResultsUrl, query]);
  return /*#__PURE__*/React__default.createElement(FlyoutContext, {
    value: {
      siteAnalyticsEventName: siteAnalyticsEventName,
      analyticsQueryString: getSiteAnalyticsQueryString(query)
    }
  }, (isFocused || selectedResult !== 0) && /*#__PURE__*/React__default.createElement(React__default.Fragment, null, veilEnabled && isFlyoutExpanded && /*#__PURE__*/React__default.createElement(Box, {
    className: "search-flyout-veil",
    mt: 2,
    style: {
      backgroundColor: tokens.SwanSemColorBrandMidnight,
      opacity: 0.85
    },
    z: tokens.SwanSemZIndexOverlay
  }), /*#__PURE__*/React__default.createElement(Box, {
    id: "siteSearchResultsListBox-".concat(instanceId),
    className: "search-flyout-wrapper",
    paddingTop: flyoutPaddingPixels,
    width: "100%"
  }, /*#__PURE__*/React__default.createElement(VisuallyHidden, {
    id: "siteHeaderSearchFlyoutLabel-".concat(instanceId),
    "aria-live": "polite",
    "aria-atomic": "true",
    role: "status"
  }, results && Object.keys(results).length > 0 && /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(VisuallyHidden, {
    className: "r-".concat(query),
    "aria-hidden": "true"
  }, query), localize('searchFlyoutLabel', locale))), /*#__PURE__*/React__default.createElement("div", {
    className: "search-flyout"
  }, /*#__PURE__*/React__default.createElement(GridContainer, {
    id: "siteHeaderSearchFlyout",
    className: "search-flyout-drop-down",
    display: isFlyoutExpanded ? 'block' : 'none',
    style: {
      borderRadius: tokens.SwanSemBorderRadiusStrong
    },
    marginTop: {
      sm: 2,
      md: 2,
      lg: 2,
      xl: 2,
      xs: 0
    },
    bgc: 'standard',
    p: 0
  }, results && /*#__PURE__*/React__default.createElement(SearchResultsDisplay, {
    resultDisplayConfiguration: _objectSpread2(_objectSpread2({}, displayConfigurationDecider(applicationName)), resultDisplayConfiguration)
  }), results && allResultsUrl && query.length >= minQueryLen && /*#__PURE__*/React__default.createElement(FlyoutFooter, {
    seeAllText: seeAllText
  }))))));
};

export { FlyoutContext, FlyoutFooter, SearchFlyout as default, useFlyoutContext };
//# sourceMappingURL=SearchFlyout.js.map
